import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { background, black, blue, divider, text } from "@/theme/colors";
import { ClearIcon } from "@mui/x-date-pickers";
import { initJobWorkingTimeForm } from "@/features/job/utils/convertJobFormData";
import { InputTimePicker } from "@/components/elements/InputTimePicker";
import { calculationWorkingTime } from "@/features/job/utils/jobWorkingTime";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useDialog } from "@/hooks/useDialog";

type TJobWorkingConditionProps = {
  isDraft?: boolean;
  bordered?: boolean;
};

type TTotalTime = {
  hours: number;
  minutes: number;
};

export const JobWorkingCondition: FC<TJobWorkingConditionProps> = ({ isDraft, bordered = true }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const confirmRemove = useDialog();
  const [totalTime, setTotalTime] = useState<TTotalTime[]>([
    {
      hours: 0,
      minutes: 0,
    },
  ]);

  const jobWorkingTimesWatchField = useWatch({
    control,
    name: "jobWorkingTimes",
  });

  const getTotalTimesByIndex = (index: number, key: keyof TTotalTime) => {
    if (totalTime[index]) {
      return totalTime[index][key];
    }
    return 0;
  };

  const renderTotalTime = (index: number, key: keyof TTotalTime) => {
    if (jobWorkingTimesWatchField) {
      const breakTime = jobWorkingTimesWatchField[index].workingTimeBreakTime;
      if (!breakTime || breakTime === 0) {
        return "";
      }
      const value = getTotalTimesByIndex(index, key);
      if (value === 0) {
        const hourValue = getTotalTimesByIndex(index, "hours");
        if (hourValue && key === "minutes") {
          return value;
        }
        return "";
      }
      return value;
    }
    return "";
  };

  const handleAddJobWorkingTimes = () => {
    if (jobWorkingTimesWatchField) {
      setValue("jobWorkingTimes", [...jobWorkingTimesWatchField, { ...initJobWorkingTimeForm }]);
      setTotalTime([...totalTime, { hours: 0, minutes: 0 }]);
    }
  };

  const handleRemoveJobWorkingTime = async () => {
    if (jobWorkingTimesWatchField) {
      const lastIndex = jobWorkingTimesWatchField.length - 1;
      await handleRemoveJobWorkingTimeByIndex(lastIndex);
    }
  };

  const hasCompleteWorkingTimeData = (index: number): boolean => {
    if (!jobWorkingTimesWatchField) return false;
    const workingTime = jobWorkingTimesWatchField?.[index];
    return !!(workingTime.workingTimeBreakTime || workingTime.workingTimeStart || workingTime.workingTimeEnd);
  };

  const removeJobWorkingTime = (index: number): void => {
    if (!jobWorkingTimesWatchField) return;

    const updatedSchedules = jobWorkingTimesWatchField.filter((_, i) => i !== index);
    setValue("jobWorkingTimes", updatedSchedules);

    setTotalTime((prevTotalTime) => prevTotalTime.filter((_, i) => i !== index));
  };

  const handleRemoveJobWorkingTimeByIndex = async (index: number): Promise<void> => {
    if (!jobWorkingTimesWatchField) return;

    const shouldConfirm = hasCompleteWorkingTimeData(index);

    if (shouldConfirm) {
      const confirmed = await confirmRemove.show();
      if (!confirmed) return;
    }

    removeJobWorkingTime(index);
  };

  const handleCalculationJobWorkingTime = useCallback(() => {
    if (jobWorkingTimesWatchField && jobWorkingTimesWatchField.length > 0) {
      const calculatedTimes = calculationWorkingTime(jobWorkingTimesWatchField);

      setTotalTime(calculatedTimes);
    } else {
      setTotalTime([
        {
          hours: 0,
          minutes: 0,
        },
      ]);
    }
  }, [jobWorkingTimesWatchField]);

  useEffect(() => {
    handleCalculationJobWorkingTime();
  }, [handleCalculationJobWorkingTime]);

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="勤務条件" />} showDivider={false} bordered={bordered} padding={!bordered ? 0 : "24px"}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="workplace" required={!isDraft}>
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="workplaceDetail">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          {(jobWorkingTimesWatchField ?? []).map((workingTime, index) => (
            <React.Fragment key={index}>
              <InnerBox
                showDivider={false}
                title={<Typography variant="sub16Bold">労働時間{index + 1}</Typography>}
                sx={{
                  width: "100%",
                  marginLeft: "18px",
                  marginTop: "16px",
                }}
              >
                <Grid item xs={12}>
                  <Stack flexDirection="row" alignItems="flex-start" gap={0.5}>
                    <FormField
                      control={control}
                      label={`始業`}
                      id={`jobWorkingTimes-${index}-workingTimeStart`}
                      name={`jobWorkingTimes.${index}.workingTimeStart`}
                      errorMessage={methods.getFieldState(`jobWorkingTimes.${index}.workingTimeStart`).error?.message}
                      required={!isDraft}
                    >
                      <InputTimePicker readonly={false} />
                    </FormField>
                    <Typography variant="body14" position="relative" top={28} marginBottom={0.75}>
                      ～
                    </Typography>
                    <FormField
                      control={control}
                      label={`終業`}
                      id={`jobWorkingTimes-${index}-workingTimeEnd`}
                      name={`jobWorkingTimes.${index}.workingTimeEnd`}
                      errorMessage={methods.getFieldState(`jobWorkingTimes.${index}.workingTimeEnd`).error?.message}
                      required={!isDraft}
                    >
                      <InputTimePicker readonly={false} />
                    </FormField>
                  </Stack>
                  <Stack flexDirection="row" alignItems="flex-start" gap={3} mt={2}>
                    <FormField
                      control={control}
                      label={`休憩時間`}
                      id={`jobWorkingTimes-${index}-workingTimeBreakTime`}
                      name={`jobWorkingTimes.${index}.workingTimeBreakTime`}
                      errorMessage={methods.getFieldState(`jobWorkingTimes.${index}.workingTimeBreakTime`).error?.message}
                    >
                      <Input endAdornment="分" />
                    </FormField>
                    <FormField label={`所定労働時間（自動計算）`}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        border={`1px solid ${divider.middle}`}
                        bgcolor={background.disable}
                        borderRadius={1}
                        sx={{ pointerEvents: "none" }}
                        px={1}
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0, marginLeft: "40px" }}>
                            {renderTotalTime(index, "hours")}
                          </Typography>
                          <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0, marginLeft: "40px" }}>
                            時間
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={1} height={30}>
                          <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0 }}>
                            {renderTotalTime(index, "minutes")}
                          </Typography>
                          <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0 }}>
                            分
                          </Typography>
                        </Stack>
                      </Stack>
                    </FormField>
                  </Stack>
                </Grid>
              </InnerBox>
              {jobWorkingTimesWatchField && index + 1 < jobWorkingTimesWatchField.length && (
                <Grid paddingTop="0 !important" item xs={12} mt={1}>
                  <Stack flexDirection="row" alignItems="center" gap={1.5}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleRemoveJobWorkingTimeByIndex(index)}
                    >
                      <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                      <Typography variant="cap12Light">削除</Typography>
                    </Stack>
                  </Stack>
                </Grid>
              )}
            </React.Fragment>
          ))}
          {jobWorkingTimesWatchField && (
            <Grid paddingTop="0 !important" item xs={12} mt={1}>
              <Stack flexDirection="row" alignItems="center" gap={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={handleAddJobWorkingTimes}>
                  <AddCircleOutlineIcon
                    sx={{
                      color: blue[80],
                      fontSize: 18,
                    }}
                  />
                  <Typography variant="cap12">始業～所定労働時間を追加</Typography>
                </Stack>
                {jobWorkingTimesWatchField?.length && jobWorkingTimesWatchField.length > 1 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={handleRemoveJobWorkingTime}>
                    <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                    <Typography variant="cap12Light">削除</Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
          )}
          <Grid item xs={6}>
            <FormField control={control} name="regularHolidays">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="irregularHolidays">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="totalAnnualHolidays">
              <Input placeholder="入力してください" commasFormat={false} decimalPlaces={1} endAdornment="日" />
            </FormField>
          </Grid>
        </Grid>
        <ConfirmSaveModal title="削除の確認" open={confirmRemove.open} onOk={confirmRemove.confirm} onClose={confirmRemove.cancel}>
          <Typography component="p" variant="subtitle1">
            「労働時間」を削除してもよろしいですか？
          </Typography>
        </ConfirmSaveModal>
      </InnerBox>
    </Stack>
  );
};
